import * as React from 'react';
import { defaults, reject, sortBy } from 'lodash';
import type { UploadingFileState } from '@atlaskit/media-client';
import { MediaClient } from '@atlaskit/media-client';
import { Dropzone, Browser, Clipboard, isImagePreview } from '@atlaskit/media-picker';
import type {
    UploadEndEventPayload,
    UploadPreviewUpdateEventPayload,
    UploadErrorEventPayload,
    UploadsStartEventPayload,
    MediaFile,
} from '@atlaskit/media-picker/types';
import { reportError } from '@atlassian/help-center-common-util/sentry';
import { AttachmentsDumb } from './attachments-dumb';
import type { AttachmentsProps, AttachmentsState, AttachmentSource, AttachmentFileInProgress } from './types';

const addOrUpdateInProgress = (state: AttachmentsState, ...newFiles: AttachmentFileInProgress[]) => {
    const updatedFiles = newFiles.map((newFile) => {
        const oldFile = state.filesInProgress.find((file) => file.id === newFile.id);
        if (oldFile) {
            return defaults({}, newFile, oldFile);
        }
        return newFile;
    });
    const remainingValue = reject(
        state.filesInProgress,
        (file) => newFiles.find((newFile) => newFile.id === file.id) !== undefined
    );
    return sortBy(remainingValue.concat(updatedFiles), [
        (sortingFile: AttachmentFileInProgress) => sortingFile.name,
        (sortingFile: AttachmentFileInProgress) => sortingFile.id,
    ]);
};

class Attachments extends React.PureComponent<AttachmentsProps, AttachmentsState> {
    state: AttachmentsState = {
        failures: [],
        filesInProgress: [],
        uploaded: [],
        isBrowserOpen: false,
        prevAuthProvider: null,
        mediaClientConfig: null,
    };

    private cancelFunctions: Map<AttachmentSource, (uploadId: string) => void> = new Map();

    private get uploadParams() {
        return {
            collection: this.props.collection,
        };
    }

    private onUploadStarted(mediaComponent?: AttachmentSource) {
        return (data: UploadsStartEventPayload) => {
            // Remove the old uploads and replace with the new ones.
            const files = data.files.map((file) => ({
                id: file.id,
                name: file.name,
                size: file.size,
                mimeType: file.type,
                source: mediaComponent,
            }));

            // Call onUploadStatusUpdate each time there is progress to report on a file
            if (this.state.mediaClientConfig !== null) {
                const mediaClient = new MediaClient(this.state.mediaClientConfig);
                data.files.forEach((file) => {
                    const subscription = mediaClient.file
                        .getFileState(file.id, { collectionName: this.props.collection })
                        .subscribe({
                            next: (fileState) => {
                                if (fileState.status !== 'uploading') {
                                    subscription.unsubscribe();
                                    return;
                                }
                                this.onUploadStatusUpdate(fileState as UploadingFileState);
                            },
                            error: (error) => {
                                if (error !== 'canceled') {
                                    reportError(error, { message: `Failed uploading file.` });
                                }

                                subscription.unsubscribe();
                            },
                        });
                });
            }

            this.setState((state) => ({ filesInProgress: addOrUpdateInProgress(state, ...files) }));
            this.props.blockFormSubmission && this.props.blockFormSubmission(true);
        };
    }

    private onUploadStatusUpdate = (data: UploadingFileState) => {
        this.setState((state) => ({
            filesInProgress: addOrUpdateInProgress(state, {
                id: data.id,
                name: data.name,
                size: data.size,
                mimeType: data.mimeType,
                progress: Math.ceil(data.progress * 100),
            }),
        }));
    };

    private onUploadPreviewUpdate = (data: UploadPreviewUpdateEventPayload) => {
        this.setState((state) => ({
            filesInProgress: addOrUpdateInProgress(state, {
                id: data.file.id,
                name: data.file.name,
                size: data.file.size,
                mimeType: data.file.type,
                // @ts-ignore TypeScript upgrade 4.7.4
                preview: isImagePreview(data.preview) ? URL.createObjectURL(data.preview.file) : undefined,
            }),
        }));
    };

    private onUploadCompleted = (data: UploadEndEventPayload) => {
        this.props.saveAttachment &&
            this.props.saveAttachment(data.file.id).subscribe(
                () => this.onAttachmentSaved(data.file),
                (error: string) => this.onSaveAttachmentFailed(data.file, error)
            );
    };

    private onUploadError = (data: UploadErrorEventPayload) => {
        this.setState((state) => {
            const fileInProgress = state.filesInProgress.find((file) => file.id === data.fileId);
            if (fileInProgress) {
                return {
                    filesInProgress: reject(state.filesInProgress, (file) => file.id === data.fileId),
                    failures: state.failures.concat([
                        {
                            id: data.fileId,
                            name: fileInProgress.name,
                            size: fileInProgress.size,
                            mimeType: fileInProgress.mimeType,
                            error: data.error.description,
                        },
                    ]),
                };
            }
            return null;
        });
    };

    private onAttachmentSaved = (savedFile: MediaFile) => {
        this.setState(
            (state) => ({
                uploaded: (state.uploaded ?? []).concat([
                    {
                        id: savedFile.id,
                        name: savedFile.name,
                        size: savedFile.size,
                        mimeType: savedFile.type,
                    },
                ]),
                filesInProgress: reject(state.filesInProgress, (file) => file.id === savedFile.id),
            }),
            () => {
                this.props.onChange?.(this.state.uploaded);
            }
        );
    };

    private onSaveAttachmentFailed = (savedFile: MediaFile, errors: string) => {
        this.setState((state) => ({
            filesInProgress: reject(state.filesInProgress, (file) => file.id === savedFile.id),
            failures: state.failures.concat([
                {
                    id: savedFile.id,
                    name: savedFile.name,
                    size: savedFile.size,
                    mimeType: savedFile.type,
                    error: errors,
                },
            ]),
        }));
    };

    private onCancelUpload = (fileId: string, source?: AttachmentSource) => {
        if (source) {
            this.cancelFunctions.get(source)?.(fileId);
        }
        this.setState((state) => ({
            filesInProgress: reject(state.filesInProgress, (file) => file.id === fileId),
        }));
    };

    private onIgnoreError = (fileId: string) => {
        this.setState((state) => ({
            failures: reject(state.failures, (file) => file.id === fileId),
        }));
    };

    private onRemoveFile = (fileId: string) => {
        this.setState(
            (state) => ({ uploaded: reject(state.uploaded, (file) => file.id === fileId) }),
            () => {
                this.props.onChange?.(this.state.uploaded);
            }
        );
    };

    private showFileBrowser = () => {
        this.setState({ isBrowserOpen: true });
    };

    private onBrowserClose = () => {
        this.setState({ isBrowserOpen: false });
    };

    private saveCancelFunction = (source: AttachmentSource) => (cancel: (uploadId: string) => void) => {
        this.cancelFunctions.set(source, cancel);
    };

    static getDerivedStateFromProps(
        props: Readonly<AttachmentsProps>,
        state: AttachmentsState
    ): Partial<AttachmentsState> | null {
        // Need to ensure that mediaClientConfig doesn't change between renders as otherwise
        // getMediaClient() won't give us the same media client used by the media-picker components
        if (props.authProvider !== state.prevAuthProvider) {
            return {
                prevAuthProvider: props.authProvider,
                mediaClientConfig: {
                    authProvider: props.authProvider,
                    useSha256ForUploads: true,
                },
            };
        }
        return null;
    }

    componentDidUpdate() {
        if (!this.state.filesInProgress.length) {
            this.props.blockFormSubmission?.(false);
        }
    }

    render() {
        const { mediaClientConfig } = this.state;

        if (mediaClientConfig === null) {
            return null;
        }

        return (
            <>
                {!this.props.disabled && (
                    <Clipboard
                        config={{ uploadParams: this.uploadParams }}
                        mediaClientConfig={mediaClientConfig}
                        onError={this.onUploadError}
                        onPreviewUpdate={this.onUploadPreviewUpdate}
                        onEnd={this.onUploadCompleted}
                        onUploadsStart={this.onUploadStarted()}
                    />
                )}

                {!this.props.disabled && (
                    <Dropzone
                        config={{
                            // Upgrading @atlaskit/platform; fix these violations.
                            // eslint-disable-next-line @atlassian/ssr-linting/no-dom-globals-in-react-cc-render,@atlaskit/platform/no-direct-document-usage
                            container: document.body,
                            uploadParams: this.uploadParams,
                        }}
                        mediaClientConfig={mediaClientConfig}
                        onCancelFn={this.saveCancelFunction('dropzone')}
                        onError={this.onUploadError}
                        onPreviewUpdate={this.onUploadPreviewUpdate}
                        onEnd={this.onUploadCompleted}
                        onUploadsStart={this.onUploadStarted('dropzone')}
                    />
                )}

                <AttachmentsDumb
                    mediaClientConfig={mediaClientConfig}
                    disabled={this.props.disabled}
                    hasError={this.props.hasError}
                    uploadedAttachments={this.state.uploaded || []}
                    uploadsInProgress={this.state.filesInProgress}
                    failedUploads={this.state.failures}
                    collection={this.props.collection}
                    onBrowseClicked={this.showFileBrowser}
                    onRemoveAttached={this.onRemoveFile}
                    onRemoveError={this.onIgnoreError}
                    onRemoveInProgress={this.onCancelUpload}
                />

                <Browser
                    onCancelFn={this.saveCancelFunction('browser')}
                    isOpen={this.state.isBrowserOpen}
                    onClose={this.onBrowserClose}
                    mediaClientConfig={mediaClientConfig}
                    config={{ multiple: true, uploadParams: this.uploadParams }}
                    onUploadsStart={this.onUploadStarted('browser')}
                    onPreviewUpdate={this.onUploadPreviewUpdate}
                    onEnd={this.onUploadCompleted}
                    onError={this.onUploadError}
                />
            </>
        );
    }
}

export default Attachments;
